import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './context/ToastContext';
import ToastContainer from './components/ToastContainer';
import ProtectedRoute from './components/ProtectedRoute';
import PublicRoute from './components/PublicRoute';
import SubdomainGuard from './components/SubdomainGuard';
import GA4PageView from './components/GA4PageView';
import ScrollToTop from './components/ScrollToTop';
import LandingPage from './pages/LandingPage';
import './App.css';

// Only the landing page is bundled with the initial chunk. Every other
// route is loaded on demand via React.lazy, so a visitor to / doesn't
// have to download Dashboard, payroll forms, charts, comparison pages,
// etc. before they can paint the homepage. Pre-fix bundle was ~1.7MB
// raw / 524KB gzipped; this targets the landing chunk dropping to
// around a third of that.
//
// `webpackPrefetch: true` tells webpack to emit
// `<link rel="prefetch">` for these chunks, so the browser fetches
// them at idle time *after* the homepage has painted. We prefetch only
// the 7 chunks users are most likely to navigate to next from the
// landing page (auth CTAs + Pricing + the 4 feature deep-dives the
// Feature cards link to). When the user clicks, the chunk is already
// in cache and the navigation feels instant. Deeper SEO pages (vs,
// alternative, glossary, blog) are not prefetched — the bytes are not
// worth it for users who will never click through.
const SignIn               = lazy(() => import(/* webpackPrefetch: true */ './pages/SignIn'));
const SignUp               = lazy(() => import(/* webpackPrefetch: true */ './pages/SignUp'));
const Dashboard            = lazy(() => import('./pages/Dashboard'));
const ForgotPassword       = lazy(() => import('./pages/ForgotPassword'));
const AcceptInvitation     = lazy(() => import('./pages/AcceptInvitation'));
const CompleteProfile      = lazy(() => import('./pages/CompleteProfile'));
const OrgChartPage         = lazy(() => import('./pages/OrgChartPage'));
const PricingPage          = lazy(() => import(/* webpackPrefetch: true */ './pages/PricingPage'));
const PayrollSoftwarePage  = lazy(() => import(/* webpackPrefetch: true */ './pages/PayrollSoftwarePage'));
const LeaveManagementPage  = lazy(() => import(/* webpackPrefetch: true */ './pages/LeaveManagementPage'));
const EmployeeManagementPage = lazy(() => import(/* webpackPrefetch: true */ './pages/EmployeeManagementPage'));
const InvoiceManagementPage = lazy(() => import(/* webpackPrefetch: true */ './pages/InvoiceManagementPage'));
const VsBambooHRPage       = lazy(() => import('./pages/comparisons/VsBambooHRPage'));
const VsGustoPage          = lazy(() => import('./pages/comparisons/VsGustoPage'));
const VsRipplingPage       = lazy(() => import('./pages/comparisons/VsRipplingPage'));
const VsZohoPeoplePage     = lazy(() => import('./pages/comparisons/VsZohoPeoplePage'));
const BambooHRAlternativePage   = lazy(() => import('./pages/alternatives/BambooHRAlternativePage'));
const GustoAlternativePage      = lazy(() => import('./pages/alternatives/GustoAlternativePage'));
const RipplingAlternativePage   = lazy(() => import('./pages/alternatives/RipplingAlternativePage'));
const ZohoPeopleAlternativePage = lazy(() => import('./pages/alternatives/ZohoPeopleAlternativePage'));
const ForStartupsPage           = lazy(() => import('./pages/solutions/ForStartupsPage'));
const ForAgenciesPage           = lazy(() => import('./pages/solutions/ForAgenciesPage'));
const ForSmallBusinessPage      = lazy(() => import('./pages/solutions/ForSmallBusinessPage'));
const SecurityPage              = lazy(() => import('./pages/SecurityPage'));
const AboutPage                 = lazy(() => import('./pages/AboutPage'));
const ChangelogPage             = lazy(() => import('./pages/ChangelogPage'));
const TermsPage                 = lazy(() => import('./pages/TermsPage'));
const PrivacyPage               = lazy(() => import('./pages/PrivacyPage'));
const BlogIndexPage             = lazy(() => import('./pages/blog/BlogIndexPage'));
const HrmsVsHrisPost            = lazy(() => import('./pages/blog/HrmsVsHrisPost'));
const LeavePolicyPost           = lazy(() => import('./pages/blog/LeavePolicyPost'));
const OnboardingChecklistPost   = lazy(() => import('./pages/blog/OnboardingChecklistPost'));
const PayslipGuidePost          = lazy(() => import('./pages/blog/PayslipGuidePost'));
const RolesVsPermissionsPost    = lazy(() => import('./pages/blog/RolesVsPermissionsPost'));
const MultiCurrencyInvoicingPost = lazy(() => import('./pages/blog/MultiCurrencyInvoicingPost'));
const RemoteTimeOffPost         = lazy(() => import('./pages/blog/RemoteTimeOffPost'));
const GlossaryIndexPage         = lazy(() => import('./pages/glossary/GlossaryIndexPage'));
const HrmsEntry                 = lazy(() => import('./pages/glossary/HrmsEntry'));
const HrisEntry                 = lazy(() => import('./pages/glossary/HrisEntry'));
const PayrollEntry              = lazy(() => import('./pages/glossary/PayrollEntry'));
const PayslipEntry              = lazy(() => import('./pages/glossary/PayslipEntry'));
const PtoEntry                  = lazy(() => import('./pages/glossary/PtoEntry'));
const OnboardingEntry           = lazy(() => import('./pages/glossary/OnboardingEntry'));
const EmployeeHandbookEntry     = lazy(() => import('./pages/glossary/EmployeeHandbookEntry'));
const LeavePolicyEntry          = lazy(() => import('./pages/glossary/LeavePolicyEntry'));
const NotFoundPage         = lazy(() => import('./pages/NotFoundPage'));

// Tiny loading state for the Suspense boundary. Intentionally blank +
// mint background — flashes for <100ms on most navigations, so a
// spinner would feel busier than nothing.
function RouteLoader() {
  return (
    <div
      aria-hidden
      style={{
        minHeight: '100vh',
        background: 'var(--paper, #F5F4F0)',
      }}
    />
  );
}

function App() {
  return (
    <HelmetProvider>
    <Router>
      <ScrollToTop />
      <GA4PageView />
      <SubdomainGuard>
      <ThemeProvider>
      <ToastProvider>
      <AuthProvider>
        <div className="App">
          <ToastContainer />
          <Suspense fallback={<RouteLoader />}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/payroll-software" element={<PayrollSoftwarePage />} />
              <Route path="/leave-management" element={<LeaveManagementPage />} />
              <Route path="/employee-management" element={<EmployeeManagementPage />} />
              <Route path="/invoice-management" element={<InvoiceManagementPage />} />
              {/* Competitor comparisons */}
              <Route path="/vs/bamboohr" element={<VsBambooHRPage />} />
              <Route path="/vs/gusto" element={<VsGustoPage />} />
              <Route path="/vs/rippling" element={<VsRipplingPage />} />
              <Route path="/vs/zoho-people" element={<VsZohoPeoplePage />} />
              {/* Alternative pages — brand-jacking */}
              <Route path="/bamboohr-alternative" element={<BambooHRAlternativePage />} />
              <Route path="/gusto-alternative" element={<GustoAlternativePage />} />
              <Route path="/rippling-alternative" element={<RipplingAlternativePage />} />
              <Route path="/zoho-people-alternative" element={<ZohoPeopleAlternativePage />} />
              {/* Solutions / personas */}
              <Route path="/for-startups" element={<ForStartupsPage />} />
              <Route path="/for-agencies" element={<ForAgenciesPage />} />
              <Route path="/for-small-business" element={<ForSmallBusinessPage />} />
              {/* Trust pages */}
              <Route path="/security" element={<SecurityPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/changelog" element={<ChangelogPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              {/* Blog */}
              <Route path="/blog" element={<BlogIndexPage />} />
              <Route path="/blog/hrms-vs-hris" element={<HrmsVsHrisPost />} />
              <Route path="/blog/how-to-write-a-leave-policy" element={<LeavePolicyPost />} />
              <Route path="/blog/onboarding-checklist-first-10-employees" element={<OnboardingChecklistPost />} />
              <Route path="/blog/what-goes-on-a-payslip" element={<PayslipGuidePost />} />
              <Route path="/blog/roles-vs-permissions-small-team" element={<RolesVsPermissionsPost />} />
              <Route path="/blog/multi-currency-invoicing-small-agencies" element={<MultiCurrencyInvoicingPost />} />
              <Route path="/blog/remote-team-time-off-tracking" element={<RemoteTimeOffPost />} />
              {/* Glossary cluster */}
              <Route path="/glossary" element={<GlossaryIndexPage />} />
              <Route path="/glossary/hrms" element={<HrmsEntry />} />
              <Route path="/glossary/hris" element={<HrisEntry />} />
              <Route path="/glossary/payroll" element={<PayrollEntry />} />
              <Route path="/glossary/payslip" element={<PayslipEntry />} />
              <Route path="/glossary/pto" element={<PtoEntry />} />
              <Route path="/glossary/onboarding" element={<OnboardingEntry />} />
              <Route path="/glossary/employee-handbook" element={<EmployeeHandbookEntry />} />
              <Route path="/glossary/leave-policy" element={<LeavePolicyEntry />} />
              <Route
                path="/signin"
                element={
                  <PublicRoute>
                    <SignIn />
                  </PublicRoute>
                }
              />
              <Route
                path="/signup"
                element={
                  <PublicRoute>
                    <SignUp />
                  </PublicRoute>
                }
              />
              <Route
                path="/dashboard/*"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/org-chart"
                element={
                  <ProtectedRoute>
                    <OrgChartPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/forgot-password"
                element={
                  <PublicRoute>
                    <ForgotPassword />
                  </PublicRoute>
                }
              />
              {/* Invitation acceptance - public route */}
              <Route
                path="/auth/invitation/accept"
                element={<AcceptInvitation />}
              />
              {/* Profile completion - requires auth token but not full profile */}
              <Route
                path="/complete-profile"
                element={<CompleteProfile />}
              />
              {/* Catch-all: branded 404 with noindex */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </div>
      </AuthProvider>
      </ToastProvider>
      </ThemeProvider>
      </SubdomainGuard>
    </Router>
    </HelmetProvider>
  );
}

export default App;
